import {MATCH_RESULTS} from "../../../util/Constants";
import type {
  Match,
  Player,
  PlayerAwardsContext,
  PlayerScore,
  TournamentDetail,
} from "../../../types/domain";

export interface FunFact {
  key: string;
  params?: Record<string, string | number>;
}

export interface TournamentAggregates {
  /** playerId -> total Elo delta in this tournament. */
  eloGainByPlayer: Map<number, number>;
  /** playerId of the (any) player with the highest pre-tournament Elo in this tournament. */
  highestEntryEloPlayerId: number | null;
  highestEntryElo: number;
  /** playerIds with top Elo gain in this tournament (could be tied). */
  topEloGainPlayerIds: Set<number>;
  topEloGain: number;
  /** playerId -> count of sets with total points >= 20. */
  longSetCounts: Map<number, number>;
  /** playerId -> count of won sets decided by 2-or-fewer points (10:12 etc). */
  closeSetWinCounts: Map<number, number>;
  /** playerId -> count of won matches where the player lost the first set. */
  comebackWinCounts: Map<number, number>;
  /** playerId -> count of MATCHES that went to a decisive set (only counts the player's wins). */
  decisiveWinCounts: Map<number, number>;
  /** Match with the most total points in the tournament. */
  longestMatchPoints: number;
  longestMatchPlayerIds: Set<number>;
  /** Single set with the most total points in the tournament. */
  longestSetPoints: number;
  longestSetPlayerIds: Set<number>;
  /** playerId -> biggest positive (opponent_elo - player_elo) deficit where the player won. */
  biggestUpsets: Map<number, number>;
  /** playerIds that have at least one match in tournament.topMatches. */
  playersInTopMatches: Set<number>;
  /** playerId -> # of finished matches the player played. */
  matchCountByPlayer: Map<number, number>;
  /** playerId -> # of wins WITHOUT a decisive set (won in straight sets / 2:0 etc.). */
  straightWinCounts: Map<number, number>;
  /** playerId -> # of the player's matches that went to a decisive set (wins AND losses). */
  decisiveMatchCounts: Map<number, number>;
  /** playerId -> longest consecutive-wins streak across this tournament's matches. */
  longestWinStreaks: Map<number, number>;
  /** Tournament winner (placement === 1) — null if not yet decided / no placements. */
  winnerPlayerId: number | null;
  /** playerIds who have a match-win against the winnerPlayerId. */
  playersWhoBeatWinner: Set<number>;
  /** Closest-match data — the match whose sets were most evenly contested. Ranked by the
   *  largest per-set point margin (smaller = tighter); ties broken by the sum of set margins. */
  closestMatchMaxSetMargin: number | null;
  closestMatchPlayerIds: Set<number>;
  /** Highest combined pre-match Elo across finished matches with both sides' Elo known. */
  highestComboElo: number | null;
  highestComboEloPlayerIds: Set<number>;
  /** Smallest absolute pre-match Elo difference across finished matches with both sides' Elo known. */
  smallestEloDiff: number | null;
  smallestEloDiffPlayerIds: Set<number>;
  /** Biggest set point margin in a single set anywhere in the tournament. */
  biggestSetMargin: number;
  biggestSetMarginWinnerIds: Set<number>;
  /** playerIds who played in the earliest finished match (by finishedAt fallback createdAt). */
  firstMatchPlayerIds: Set<number>;
  /** playerIds who played in the latest finished match. */
  lastMatchPlayerIds: Set<number>;
  /** playerIds in the tournament's most-liked match (only set if any match has likes > 0). */
  mostLikedMatchPlayerIds: Set<number>;
  mostLikedMatchLikes: number;
  /** playerId -> list of opponent IDs (one entry per match played, duplicates allowed). */
  opponentsByPlayer: Map<number, number[]>;
  /** playerId -> true if every opponent's pre-match Elo was strictly higher than this player's
   *  pre-match Elo. Requires the player to have played at least N matches with Elo data. */
  allOpponentsHigherEloByPlayer: Map<number, boolean>;
}

interface FactContext {
  player: Player;
  score: PlayerScore;
  placement: number;
  totalPlayers: number;
  aggregates: TournamentAggregates;
  awardsContext: PlayerAwardsContext | null;
  ladderNameKey: string;
  tournament: TournamentDetail;
}

type FactComputer = (ctx: FactContext) => FunFact | null;

const LONG_SET_THRESHOLD = 20;
const CLOSE_SET_MARGIN = 2;
const UPSET_ELO_THRESHOLD = 100;
const MILESTONE_LADDER_TOURNAMENTS = new Set([5, 10, 15, 20, 25, 50, 100]);
const MILESTONE_TOTAL_TOURNAMENTS = new Set([5, 10, 15, 20, 25, 50, 100]);
const RETURN_DAYS_THRESHOLD = 180;

function isPlayerWin(m: Match, playerId: number): boolean {
  if (m.state === MATCH_RESULTS.PLAYER_ONE_WON || m.state === MATCH_RESULTS.PLAYER_ONE_DEFAULT) {
    return m.playerOne.id === playerId;
  }
  if (m.state === MATCH_RESULTS.PLAYER_TWO_WON || m.state === MATCH_RESULTS.PLAYER_TWO_DEFAULT) {
    return m.playerTwo.id === playerId;
  }
  return false;
}

function isMatchFinishedAndPlayed(m: Match): boolean {
  return m.state === MATCH_RESULTS.PLAYER_ONE_WON || m.state === MATCH_RESULTS.PLAYER_TWO_WON;
}

function increment(map: Map<number, number>, key: number, by = 1): void {
  map.set(key, (map.get(key) ?? 0) + by);
}

function trackTop(map: Map<number, number>, key: number, value: number): void {
  const cur = map.get(key);
  if (cur == null || value > cur) map.set(key, value);
}

export function computeTournamentAggregates(t: TournamentDetail): TournamentAggregates {
  const allMatches: Match[] = t.rounds.flatMap(r => r.matches);

  const eloGainByPlayer = new Map<number, number>();
  const longSetCounts = new Map<number, number>();
  const closeSetWinCounts = new Map<number, number>();
  const comebackWinCounts = new Map<number, number>();
  const decisiveWinCounts = new Map<number, number>();
  const decisiveMatchCounts = new Map<number, number>();
  const biggestUpsets = new Map<number, number>();
  const matchCountByPlayer = new Map<number, number>();
  const straightWinCounts = new Map<number, number>();
  const opponentsByPlayer = new Map<number, number[]>();
  const playersWhoBeatWinner = new Set<number>();

  let longestMatchPoints = 0;
  let longestMatchPlayerIds = new Set<number>();
  let longestSetPoints = 0;
  let longestSetPlayerIds = new Set<number>();

  let closestMatchMaxSetMargin: number | null = null;
  let closestMatchSumSetMargins: number | null = null;
  let closestMatchPlayerIds = new Set<number>();
  let highestComboElo: number | null = null;
  let highestComboEloPlayerIds = new Set<number>();
  let smallestEloDiff: number | null = null;
  let smallestEloDiffPlayerIds = new Set<number>();
  let biggestSetMargin = 0;
  let biggestSetMarginWinnerIds = new Set<number>();
  let firstMatchPlayerIds = new Set<number>();
  let lastMatchPlayerIds = new Set<number>();
  let firstMatchTime: number | null = null;
  let lastMatchTime: number | null = null;
  let mostLikedMatchPlayerIds = new Set<number>();
  let mostLikedMatchLikes = 0;

  let highestEntryElo = -Infinity;
  let highestEntryEloPlayerId: number | null = null;
  for (const s of t.finalPlacements) {
    const e = s.player.elo ?? 0;
    if (e > highestEntryElo) {
      highestEntryElo = e;
      highestEntryEloPlayerId = s.player.id;
    }
  }

  const winnerPlayerId = t.finalPlacements.length > 0
    // finalPlacements is in best→worst order; first entry is the winner
    ? t.finalPlacements[0].player.id
    : null;

  for (const m of allMatches) {
    if (!isMatchFinishedAndPlayed(m)) continue;

    increment(matchCountByPlayer, m.playerOne.id);
    increment(matchCountByPlayer, m.playerTwo.id);

    // Track opponents for rematch + allOpponentsHigherElo checks
    const p1Opps = opponentsByPlayer.get(m.playerOne.id) ?? [];
    p1Opps.push(m.playerTwo.id);
    opponentsByPlayer.set(m.playerOne.id, p1Opps);
    const p2Opps = opponentsByPlayer.get(m.playerTwo.id) ?? [];
    p2Opps.push(m.playerOne.id);
    opponentsByPlayer.set(m.playerTwo.id, p2Opps);

    const e1B = m.playerOneEloBeforeMatchFinished;
    const e1A = m.playerOneEloAfterMatchFinished;
    const e2B = m.playerTwoEloBeforeMatchFinished;
    const e2A = m.playerTwoEloAfterMatchFinished;
    if (e1B != null && e1A != null) increment(eloGainByPlayer, m.playerOne.id, e1A - e1B);
    if (e2B != null && e2A != null) increment(eloGainByPlayer, m.playerTwo.id, e2A - e2B);

    // Highest combined / most balanced pre-match Elo
    if (e1B != null && e2B != null) {
      const combo = e1B + e2B;
      if (highestComboElo == null || combo > highestComboElo) {
        highestComboElo = combo;
        highestComboEloPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
      } else if (combo === highestComboElo) {
        highestComboEloPlayerIds.add(m.playerOne.id);
        highestComboEloPlayerIds.add(m.playerTwo.id);
      }
      const diff = Math.abs(e1B - e2B);
      if (smallestEloDiff == null || diff < smallestEloDiff) {
        smallestEloDiff = diff;
        smallestEloDiffPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
      } else if (diff === smallestEloDiff) {
        smallestEloDiffPlayerIds.add(m.playerOne.id);
        smallestEloDiffPlayerIds.add(m.playerTwo.id);
      }
    }

    // First / last match by time
    const matchTime = m.finishedAt ? new Date(m.finishedAt).getTime() : new Date(m.createdAt).getTime();
    if (firstMatchTime == null || matchTime < firstMatchTime) {
      firstMatchTime = matchTime;
      firstMatchPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
    } else if (matchTime === firstMatchTime) {
      firstMatchPlayerIds.add(m.playerOne.id);
      firstMatchPlayerIds.add(m.playerTwo.id);
    }
    if (lastMatchTime == null || matchTime > lastMatchTime) {
      lastMatchTime = matchTime;
      lastMatchPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
    } else if (matchTime === lastMatchTime) {
      lastMatchPlayerIds.add(m.playerOne.id);
      lastMatchPlayerIds.add(m.playerTwo.id);
    }

    // Most-liked match
    if (m.likes > mostLikedMatchLikes) {
      mostLikedMatchLikes = m.likes;
      mostLikedMatchPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
    } else if (m.likes === mostLikedMatchLikes && m.likes > 0) {
      mostLikedMatchPlayerIds.add(m.playerOne.id);
      mostLikedMatchPlayerIds.add(m.playerTwo.id);
    }

    // Match total points + winner stats
    let matchTotal = 0;
    let p1Points = 0;
    let p2Points = 0;
    let p1Sets = 0;
    let p2Sets = 0;
    let matchMaxSetMargin = 0;
    let matchSumSetMargins = 0;
    for (const r of m.results) {
      const setTotal = r.playerOneScore + r.playerTwoScore;
      matchTotal += setTotal;
      p1Points += r.playerOneScore;
      p2Points += r.playerTwoScore;
      if (r.playerOneScore > r.playerTwoScore) p1Sets++; else if (r.playerTwoScore > r.playerOneScore) p2Sets++;

      if (setTotal > LONG_SET_THRESHOLD) {
        increment(longSetCounts, m.playerOne.id);
        increment(longSetCounts, m.playerTwo.id);
      }

      if (setTotal > longestSetPoints) {
        longestSetPoints = setTotal;
        longestSetPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
      } else if (setTotal === longestSetPoints) {
        longestSetPlayerIds.add(m.playerOne.id);
        longestSetPlayerIds.add(m.playerTwo.id);
      }

      const margin = Math.abs(r.playerOneScore - r.playerTwoScore);
      if (margin > matchMaxSetMargin) matchMaxSetMargin = margin;
      matchSumSetMargins += margin;
      if (margin <= CLOSE_SET_MARGIN) {
        if (r.playerOneScore > r.playerTwoScore) increment(closeSetWinCounts, m.playerOne.id);
        else if (r.playerTwoScore > r.playerOneScore) increment(closeSetWinCounts, m.playerTwo.id);
      }

      // Biggest set margin tracking
      if (margin > biggestSetMargin) {
        biggestSetMargin = margin;
        const winnerId = r.playerOneScore > r.playerTwoScore ? m.playerOne.id
          : r.playerTwoScore > r.playerOneScore ? m.playerTwo.id : null;
        biggestSetMarginWinnerIds = winnerId != null ? new Set([winnerId]) : new Set();
      } else if (margin === biggestSetMargin && margin > 0) {
        const winnerId = r.playerOneScore > r.playerTwoScore ? m.playerOne.id
          : r.playerTwoScore > r.playerOneScore ? m.playerTwo.id : null;
        if (winnerId != null) biggestSetMarginWinnerIds.add(winnerId);
      }
    }

    // Closest match — smallest max per-set margin, tiebroken by smallest sum of set margins.
    // "Tight" means each set was contested, not that lopsided sets happened to cancel out.
    if (m.results.length > 0) {
      const isTighter = closestMatchMaxSetMargin == null
        || matchMaxSetMargin < closestMatchMaxSetMargin
        || (matchMaxSetMargin === closestMatchMaxSetMargin
            && closestMatchSumSetMargins != null
            && matchSumSetMargins < closestMatchSumSetMargins);
      const isTied = closestMatchMaxSetMargin != null
        && matchMaxSetMargin === closestMatchMaxSetMargin
        && closestMatchSumSetMargins != null
        && matchSumSetMargins === closestMatchSumSetMargins;
      if (isTighter) {
        closestMatchMaxSetMargin = matchMaxSetMargin;
        closestMatchSumSetMargins = matchSumSetMargins;
        closestMatchPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
      } else if (isTied) {
        closestMatchPlayerIds.add(m.playerOne.id);
        closestMatchPlayerIds.add(m.playerTwo.id);
      }
    }

    // Decisive match (winner one set ahead of loser AND total sets = max possible)
    const matchTotalSets = p1Sets + p2Sets;
    const isDecisive = matchTotalSets === m.setsNeededToWin * 2 - 1;
    if (isDecisive) {
      increment(decisiveMatchCounts, m.playerOne.id);
      increment(decisiveMatchCounts, m.playerTwo.id);
    }

    if (matchTotal > longestMatchPoints) {
      longestMatchPoints = matchTotal;
      longestMatchPlayerIds = new Set([m.playerOne.id, m.playerTwo.id]);
    } else if (matchTotal === longestMatchPoints) {
      longestMatchPlayerIds.add(m.playerOne.id);
      longestMatchPlayerIds.add(m.playerTwo.id);
    }

    // Match shape: comeback wins (lost first set, won match), straight wins, decisive-set wins
    const winnerId = m.state === MATCH_RESULTS.PLAYER_ONE_WON ? m.playerOne.id : m.playerTwo.id;
    const setsForWinner = m.state === MATCH_RESULTS.PLAYER_ONE_WON ? p1Sets : p2Sets;
    const setsForLoser = m.state === MATCH_RESULTS.PLAYER_ONE_WON ? p2Sets : p1Sets;

    if (m.results.length > 0) {
      const firstSet = m.results[0];
      const firstSetWinner = firstSet.playerOneScore > firstSet.playerTwoScore
        ? m.playerOne.id
        : firstSet.playerTwoScore > firstSet.playerOneScore
          ? m.playerTwo.id
          : null;
      if (firstSetWinner != null && firstSetWinner !== winnerId) {
        increment(comebackWinCounts, winnerId);
      }
    }

    if (setsForLoser === 0) {
      // Straight-sets win (e.g., 3:0 or 2:0 depending on format)
      increment(straightWinCounts, winnerId);
    } else if (setsForLoser + 1 === setsForWinner) {
      // Decisive set: loser was one set away (e.g., 3:2 or 2:1)
      increment(decisiveWinCounts, winnerId);
    }

    // Biggest upset (pre-match Elo difference where lower-Elo won)
    if (e1B != null && e2B != null) {
      if (m.state === MATCH_RESULTS.PLAYER_ONE_WON && e2B > e1B) {
        trackTop(biggestUpsets, m.playerOne.id, e2B - e1B);
      } else if (m.state === MATCH_RESULTS.PLAYER_TWO_WON && e1B > e2B) {
        trackTop(biggestUpsets, m.playerTwo.id, e1B - e2B);
      }
    }

    // Who beat the eventual winner
    if (winnerPlayerId != null) {
      if (m.state === MATCH_RESULTS.PLAYER_ONE_WON && m.playerTwo.id === winnerPlayerId) {
        playersWhoBeatWinner.add(m.playerOne.id);
      } else if (m.state === MATCH_RESULTS.PLAYER_TWO_WON && m.playerOne.id === winnerPlayerId) {
        playersWhoBeatWinner.add(m.playerTwo.id);
      }
    }
  }

  // Longest win streak per player (across this tournament's matches, chronological)
  const matchesByPlayer = new Map<number, Match[]>();
  for (const m of allMatches) {
    if (!isMatchFinishedAndPlayed(m)) continue;
    const a = matchesByPlayer.get(m.playerOne.id) ?? [];
    a.push(m);
    matchesByPlayer.set(m.playerOne.id, a);
    const b = matchesByPlayer.get(m.playerTwo.id) ?? [];
    b.push(m);
    matchesByPlayer.set(m.playerTwo.id, b);
  }
  const longestWinStreaks = new Map<number, number>();
  for (const [pid, matches] of matchesByPlayer.entries()) {
    const sorted = [...matches].sort((a, b) => {
      const ta = a.finishedAt ? new Date(a.finishedAt).getTime() : new Date(a.createdAt).getTime();
      const tb = b.finishedAt ? new Date(b.finishedAt).getTime() : new Date(b.createdAt).getTime();
      return ta - tb;
    });
    let max = 0;
    let cur = 0;
    for (const mm of sorted) {
      const won = (mm.state === MATCH_RESULTS.PLAYER_ONE_WON && mm.playerOne.id === pid)
        || (mm.state === MATCH_RESULTS.PLAYER_TWO_WON && mm.playerTwo.id === pid);
      if (won) { cur++; if (cur > max) max = cur; }
      else { cur = 0; }
    }
    longestWinStreaks.set(pid, max);
  }

  // All opponents higher Elo — requires player to have played at least 3 matches with valid pre-match Elo
  const allOpponentsHigherEloByPlayer = new Map<number, boolean>();
  for (const m of allMatches) {
    if (!isMatchFinishedAndPlayed(m)) continue;
    if (m.playerOneEloBeforeMatchFinished == null || m.playerTwoEloBeforeMatchFinished == null) continue;
    const e1 = m.playerOneEloBeforeMatchFinished;
    const e2 = m.playerTwoEloBeforeMatchFinished;
    const p1Cur = allOpponentsHigherEloByPlayer.get(m.playerOne.id);
    allOpponentsHigherEloByPlayer.set(m.playerOne.id, (p1Cur ?? true) && e2 > e1);
    const p2Cur = allOpponentsHigherEloByPlayer.get(m.playerTwo.id);
    allOpponentsHigherEloByPlayer.set(m.playerTwo.id, (p2Cur ?? true) && e1 > e2);
  }

  let topEloGain = -Infinity;
  let topEloGainPlayerIds = new Set<number>();
  for (const [id, gain] of eloGainByPlayer.entries()) {
    if (gain > topEloGain) { topEloGain = gain; topEloGainPlayerIds = new Set([id]); }
    else if (gain === topEloGain) topEloGainPlayerIds.add(id);
  }

  const playersInTopMatches = new Set<number>();
  for (const record of t.topMatches ?? []) {
    if (record.type !== "MATCH") continue;
    for (const entry of record.entries) {
      if (entry.match) {
        playersInTopMatches.add(entry.match.playerOne.id);
        playersInTopMatches.add(entry.match.playerTwo.id);
      }
    }
  }

  return {
    eloGainByPlayer,
    highestEntryEloPlayerId,
    highestEntryElo: highestEntryElo === -Infinity ? 0 : highestEntryElo,
    topEloGainPlayerIds,
    topEloGain: topEloGain === -Infinity ? 0 : topEloGain,
    longSetCounts,
    closeSetWinCounts,
    comebackWinCounts,
    decisiveWinCounts,
    decisiveMatchCounts,
    longestMatchPoints,
    longestMatchPlayerIds,
    longestSetPoints,
    longestSetPlayerIds,
    biggestUpsets,
    playersInTopMatches,
    matchCountByPlayer,
    straightWinCounts,
    longestWinStreaks,
    winnerPlayerId,
    playersWhoBeatWinner,
    closestMatchMaxSetMargin,
    closestMatchPlayerIds,
    highestComboElo,
    highestComboEloPlayerIds,
    smallestEloDiff,
    smallestEloDiffPlayerIds,
    biggestSetMargin,
    biggestSetMarginWinnerIds,
    firstMatchPlayerIds,
    lastMatchPlayerIds,
    mostLikedMatchPlayerIds,
    mostLikedMatchLikes,
    opponentsByPlayer,
    allOpponentsHigherEloByPlayer,
  };
}

/* Priority-ordered computers: first non-null wins. Each priority "slot" has multiple
 * computers and we let the order decide ties. Cross-tournament facts are highest priority
 * because they're rarest and most personal. Placement/achievement-duplicating facts
 * (won the tournament, undefeated, perfect sets, highest rating, etc.) are intentionally
 * NOT here — the gradient placement number and the achievement icons already convey those. */
const COMPUTERS: FactComputer[] = [
  // === FIRSTS — high impact, deeply personal ===

  // Their very first tournament ever
  ({awardsContext}) => awardsContext?.priorTournamentsTotal === 0
    ? {key: "funFact__firstEver"} : null,

  // First top-3 in this ladder (only if they actually achieved top-3 here)
  ({placement, awardsContext}) =>
    placement <= 3 && awardsContext != null
      && awardsContext.priorTop3InLadder === 0
      && awardsContext.priorTournamentsInLadder > 0
      ? {key: "funFact__firstTop3InLadder"} : null,

  // First tournament win in this ladder
  ({placement, awardsContext}) =>
    placement === 1 && awardsContext != null
      && awardsContext.priorWinsInLadder === 0
      && awardsContext.priorTournamentsInLadder > 0
      ? {key: "funFact__firstWinInLadder"} : null,

  // First tournament in this ladder (but they've played in others)
  ({awardsContext, ladderNameKey}) =>
    awardsContext != null
      && awardsContext.priorTournamentsInLadder === 0
      && awardsContext.priorTournamentsTotal > 0
      ? {key: "funFact__firstInLadder", params: {ladder: ladderNameKey}} : null,

  // === PERSONAL RECORDS ===

  // New best placement in this ladder (strict improvement)
  ({placement, awardsContext}) => {
    if (!awardsContext || awardsContext.priorBestPlacementInLadder == null) return null;
    return placement < awardsContext.priorBestPlacementInLadder
      ? {key: "funFact__newBestPlacement"} : null;
  },

  // New all-time Elo high in this ladder
  ({awardsContext}) => awardsContext?.isNewEloHighInLadder
    ? {key: "funFact__newEloHigh"} : null,

  // Tied previous best placement
  ({placement, awardsContext}) => {
    if (!awardsContext || awardsContext.priorBestPlacementInLadder == null) return null;
    return placement === awardsContext.priorBestPlacementInLadder
      && awardsContext.priorTournamentsInLadder >= 3
      ? {key: "funFact__tiedBestPlacement"} : null;
  },

  // === COMEBACK / VETERAN ===

  // Return after long absence (≥6 months)
  ({awardsContext}) => {
    const days = awardsContext?.daysSinceLastInLadder;
    if (days == null || days < RETURN_DAYS_THRESHOLD) return null;
    return {key: "funFact__longReturn", params: {months: Math.round(days / 30)}};
  },

  // Milestone tournament in this ladder
  ({awardsContext}) => {
    if (!awardsContext) return null;
    const count = awardsContext.priorTournamentsInLadder + 1;
    return MILESTONE_LADDER_TOURNAMENTS.has(count)
      ? {key: "funFact__ladderMilestone", params: {count}} : null;
  },

  // Career milestone (total across all ladders)
  ({awardsContext}) => {
    if (!awardsContext) return null;
    const count = awardsContext.priorTournamentsTotal + 1;
    return MILESTONE_TOTAL_TOURNAMENTS.has(count)
      ? {key: "funFact__careerMilestone", params: {count}} : null;
  },

  // === TOURNAMENT MOMENTS ===

  // Beat the eventual tournament winner (not yourself)
  ({player, aggregates}) =>
    aggregates.winnerPlayerId != null
      && aggregates.winnerPlayerId !== player.id
      && aggregates.playersWhoBeatWinner.has(player.id)
      ? {key: "funFact__beatTheWinner"} : null,

  // Entered the tournament as the top seed (highest Elo)
  ({player, aggregates}) =>
    aggregates.highestEntryEloPlayerId === player.id && aggregates.highestEntryElo > 0
      ? {key: "funFact__topSeed"} : null,

  // Beat the highest-Elo player in tournament (and that player isn't them)
  ({player, aggregates, tournament}) => {
    const topId = aggregates.highestEntryEloPlayerId;
    if (topId == null || topId === player.id) return null;
    const beatThem = tournament.rounds.some(r => r.matches.some(m => {
      if (!isMatchFinishedAndPlayed(m)) return false;
      if (m.playerOne.id === player.id && m.playerTwo.id === topId) return isPlayerWin(m, player.id);
      if (m.playerTwo.id === player.id && m.playerOne.id === topId) return isPlayerWin(m, player.id);
      return false;
    }));
    return beatThem ? {key: "funFact__beatTopSeed"} : null;
  },

  // Long win streak (≥4, beyond HAT_TRICK achievement)
  ({player, aggregates}) => {
    const streak = aggregates.longestWinStreaks.get(player.id) ?? 0;
    return streak >= 4 ? {key: "funFact__longerWinStreak", params: {count: streak}} : null;
  },

  // Every match they played went to a deciding set (≥3 matches)
  ({player, aggregates}) => {
    const played = aggregates.matchCountByPlayer.get(player.id) ?? 0;
    const decisive = aggregates.decisiveMatchCounts.get(player.id) ?? 0;
    return played >= 3 && played === decisive
      ? {key: "funFact__allMatchesDecisive", params: {count: played}} : null;
  },

  // Top Elo gain in tournament
  ({player, aggregates}) => {
    if (!aggregates.topEloGainPlayerIds.has(player.id)) return null;
    if (aggregates.topEloGain <= 0) return null;
    return {key: "funFact__topEloGain", params: {elo: Math.round(aggregates.topEloGain)}};
  },

  // Beat someone with much higher Elo (upset)
  ({player, aggregates}) => {
    const diff = aggregates.biggestUpsets.get(player.id) ?? 0;
    return diff >= UPSET_ELO_THRESHOLD
      ? {key: "funFact__upset", params: {diff: Math.round(diff)}} : null;
  },

  // Comeback specialist (≥2 matches won after losing the first set)
  ({player, aggregates}) => {
    const cnt = aggregates.comebackWinCounts.get(player.id) ?? 0;
    return cnt >= 2 ? {key: "funFact__comebackKing", params: {count: cnt}} : null;
  },

  // Played in the tournament's longest match
  ({player, aggregates}) =>
    aggregates.longestMatchPlayerIds.has(player.id) && aggregates.longestMatchPoints > 0
      ? {key: "funFact__longestMatch", params: {points: aggregates.longestMatchPoints}} : null,

  // Played the longest set of the tournament
  ({player, aggregates}) =>
    aggregates.longestSetPlayerIds.has(player.id) && aggregates.longestSetPoints >= LONG_SET_THRESHOLD
      ? {key: "funFact__longestSet", params: {points: aggregates.longestSetPoints}} : null,

  // Played the closest match of the tournament
  ({player, aggregates}) => {
    if (!aggregates.closestMatchPlayerIds.has(player.id)) return null;
    if (aggregates.closestMatchMaxSetMargin == null) return null;
    return {key: "funFact__closestMatch", params: {points: aggregates.closestMatchMaxSetMargin}};
  },

  // Played the match with the highest combined pre-match Elo
  ({player, aggregates}) =>
    aggregates.highestComboElo != null && aggregates.highestComboEloPlayerIds.has(player.id)
      ? {key: "funFact__highestEloMatch"} : null,

  // Played the most balanced match by Elo
  ({player, aggregates}) =>
    aggregates.smallestEloDiff != null
      && aggregates.smallestEloDiffPlayerIds.has(player.id)
      && aggregates.smallestEloDiff <= 50
      ? {key: "funFact__mostBalancedMatch"} : null,

  // Won the most one-sided set of the tournament
  ({player, aggregates}) =>
    aggregates.biggestSetMargin >= 5 && aggregates.biggestSetMarginWinnerIds.has(player.id)
      ? {key: "funFact__biggestSetMarginWin", params: {margin: aggregates.biggestSetMargin}} : null,

  // Many decisive-set wins (≥2 — went the distance and pulled it off)
  ({player, aggregates}) => {
    const cnt = aggregates.decisiveWinCounts.get(player.id) ?? 0;
    return cnt >= 2 ? {key: "funFact__decisiveSetHero", params: {count: cnt}} : null;
  },

  // Many close sets won (≥3 sets won by ≤2 point margin)
  ({player, aggregates}) => {
    const cnt = aggregates.closeSetWinCounts.get(player.id) ?? 0;
    return cnt >= 3 ? {key: "funFact__closeSetMaster", params: {count: cnt}} : null;
  },

  // Many long sets (≥3 sets with total points ≥20)
  ({player, aggregates}) => {
    const cnt = aggregates.longSetCounts.get(player.id) ?? 0;
    return cnt >= 3 ? {key: "funFact__longSets", params: {count: cnt}} : null;
  },

  // Every opponent had higher Elo (≥3 matches with Elo data)
  ({player, aggregates}) => {
    const opps = aggregates.opponentsByPlayer.get(player.id) ?? [];
    const flag = aggregates.allOpponentsHigherEloByPlayer.get(player.id);
    return opps.length >= 3 && flag === true ? {key: "funFact__allOpponentsHigherElo"} : null;
  },

  // Rematched the same opponent (≥2 matches against same opponent)
  ({player, aggregates}) => {
    const opps = aggregates.opponentsByPlayer.get(player.id) ?? [];
    const counts = new Map<number, number>();
    for (const o of opps) counts.set(o, (counts.get(o) ?? 0) + 1);
    const maxCount = [...counts.values()].reduce((a, b) => Math.max(a, b), 0);
    return maxCount >= 2 ? {key: "funFact__rematchedOpponent"} : null;
  },

  // Their match was the most-liked in the tournament (and got at least one like)
  ({player, aggregates}) =>
    aggregates.mostLikedMatchLikes > 0 && aggregates.mostLikedMatchPlayerIds.has(player.id)
      ? {key: "funFact__mostLikedMatch", params: {likes: aggregates.mostLikedMatchLikes}} : null,

  // All wins in straight sets (no decisive set, won ≥2 matches)
  ({player, aggregates, score}) => {
    if (score.matchesWon < 2) return null;
    const decisive = aggregates.decisiveWinCounts.get(player.id) ?? 0;
    return decisive === 0 ? {key: "funFact__cleanWins", params: {count: score.matchesWon}} : null;
  },

  // Played in the tournament's first match (chronologically)
  ({player, aggregates}) =>
    aggregates.firstMatchPlayerIds.has(player.id) ? {key: "funFact__firstMatchOfTournament"} : null,

  // Played in the tournament's last match (chronologically)
  ({player, aggregates}) =>
    aggregates.lastMatchPlayerIds.has(player.id) ? {key: "funFact__lastMatchOfTournament"} : null,

  // Last specific fact before generic fallback — triggers too often to be a high-priority hit.
  ({player, aggregates}) => aggregates.playersInTopMatches.has(player.id)
    ? {key: "funFact__topMatchFeatured"} : null,

  // Fallback — always applies (and gets randomized variants so it's not always identical)
  ({score, player}) => {
    const variants = ["funFact__generic1", "funFact__generic2", "funFact__generic3"];
    const idx = (player.id + score.matchesWon + score.setsWon) % variants.length;
    return {
      key: variants[idx],
      params: {
        matches: score.matchesWon + score.matchesLost,
        sets: score.setsWon,
        points: score.pointsWon,
      },
    };
  },
];

export function pickFunFact(ctx: FactContext): FunFact {
  for (const c of COMPUTERS) {
    const f = c(ctx);
    if (f) return f;
  }
  return {key: "funFact__generic1", params: {matches: 0, sets: 0, points: 0}};
}

export type {FactContext};
